import { getToken } from "../storage/authStorage";
import { API_BASE_URL } from "../../../config/api";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export interface Currency {
  id: number;
  currency_id: string;
  currency_code: string;
  currency_name: string;
  currency_name_formatted: string;
  currency_symbol: string;
  price_precision: number;
  currency_format: string;
  is_base_currency: number;
  exchange_rate: string;
  effective_date: string | null;
  auto_exchange_rate_enabled: number;
  created_at: string;
  updated_at: string;
}

export interface CurrenciesResponse {
  success: boolean;
  data: Currency[];
  total: number;
}

export const fetchCurrencies = async (): Promise<CurrenciesResponse> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/currencies`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

export interface ExchangeRateData {
  rate: number;
  effective_date: string | null;
  currency_id: string;
  currency_code: string | null;
}

export interface ExchangeRateResponse {
  success: boolean;
  data: ExchangeRateData;
  message?: string;
}

/** Zoho Books rate for currency_id effective on from_date (YYYY-MM-DD). */
export const fetchExchangeRate = async (
  currencyId: string,
  fromDate: string,
  signal?: AbortSignal,
): Promise<ExchangeRateResponse> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const id = encodeURIComponent(currencyId.trim());
  const qs = new URLSearchParams({ from_date: fromDate.trim() });

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/currencies/${id}/exchange-rate?${qs.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal,
      },
    );

    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new APIError(
        body.message || `HTTP error! status: ${response.status}`,
        response.status,
        body,
      );
    }

    return body as ExchangeRateResponse;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export interface CustomerGroup {
  group_id: number;
  group_name: string;
  group_description?: string;
  customer_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CustomerGroupsResponse {
  success: boolean;
  message: string;
  data: CustomerGroup[];
}

export const fetchCustomerGroups =
  async (): Promise<CustomerGroupsResponse> => {
    const token = getToken();

    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/invoices/customer-groups`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || `HTTP error! status: ${response.status}`,
          response.status,
          errorData
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        HTTP_STATUS.INTERNAL_SERVER_ERROR
      );
    }
  };

export const createCustomerGroup = async (groupData: {
  group_name: string;
  group_description?: string;
}): Promise<{
  success: boolean;
  message: string;
  data: CustomerGroup;
}> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/invoices/customer-groups`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(groupData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};
